import { RefObject, useEffect, useRef } from 'react';

const FOCUSABLE =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/*
 * Shared modal accessibility behavior: Escape closes, body scroll-lock while
 * open (released on close/unmount), initial focus into the dialog, Tab focus
 * trap, and focus return to the previously-focused element on close.
 * Overlay-click closing is wired by the modal components themselves so that
 * clicks INSIDE the dialog never close it.
 *
 * The open-time work (scroll lock, initial focus, remembering what to return
 * focus to) runs ONCE PER OPEN, never once per render. LiveConsole re-renders
 * every second for the countdown and hands the modal a fresh inline onClose
 * each time; with onClose in this effect's dependencies that meant the effect
 * re-ran every second, refocused the first element in the dialog, and the
 * browser scrolled the Workers table back to the top under the user. The
 * latest onClose is read through a ref instead.
 */
export const useModalA11y = (
  open: boolean,
  onClose: () => void,
  dialogRef: RefObject<HTMLElement>,
): void => {
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;

    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const dialog = dialogRef.current;
    const focusables = dialog
      ? Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE))
      : [];
    (focusables[0] ?? dialog)?.focus?.();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key === 'Tab' && dialog) {
        const items = Array.from(
          dialog.querySelectorAll<HTMLElement>(FOCUSABLE),
        );
        if (items.length === 0) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused.current?.focus?.();
    };
  }, [open, dialogRef]);
};
